import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  getStep1Questions,
  getInitialAllowancesQuestions,
  initialAllowancesQuestions,
  stateOfDomicileInitialQuestion,
  getExemptionOptions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function IowaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  /// Step1Form ///
  const step1Form = useRef();
  const step1QuestionsDataByVersion = getStep1Questions(version);
  const step1InitialQuestions = getInitialQuestions(step1QuestionsDataByVersion);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  // if the saved data for filing status is married_filing_jointly, then render spouse earned income question
  const renderSpouseEarnedIncomeQnKeys = ['married_filing_jointly', 'married_filing_jointly_or_qualifying_surviving_spouse'];
  const versionHasSpouseQn = ['11_28_2023', '11_18_2024'].includes(version);
  const [renderSpouseEarnedIncomeQn, setRenderSpouseEarnedIncomeQn] = useState(renderSpouseEarnedIncomeQnKeys.includes(data.filing_status) || false);
  const [isSpouseEarnedIncome, setIsSpouseEarnedIncome] = useState(data.spouse_earned_income || false);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const allowancesForm = useRef();
  const initialAllowancesQuestionsByVersion = getInitialAllowancesQuestions(version);
  const allowancesQuestionsWithAnswer = getInitialQuestions(initialAllowancesQuestionsByVersion);
  const [allowancesQuestions, setAllowancesQuestions] = useState(allowancesQuestionsWithAnswer);

  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const domicileForm = useRef();
  const domicileQuestionWithAnswer = getInitialQuestions(stateOfDomicileInitialQuestion);
  const [domicileQuestion, setDomicileQuestion] = useState(domicileQuestionWithAnswer);

  // to calculate total number of allowances
  const calculateAllowances = () => {
    let value = 0;

    const personalAllowances = allowancesQuestions[0].answer.answer.personal_allowances;
    if (/^\d+$/.test(personalAllowances)) value += parseInt(personalAllowances, 10);

    const dep = allowancesQuestions[1].answer.answer.dependents;
    if (/^\d+$/.test(dep)) value += parseInt(dep, 10);

    const itemizedDeductions = allowancesQuestions[2].answer.answer.itemized_deductions;
    if (/^\d+$/.test(itemizedDeductions)) value += parseInt(itemizedDeductions, 10);

    const adjustments = allowancesQuestions[3].answer.answer.adjustments_to_income;
    if (/^\d+$/.test(adjustments)) value += parseInt(adjustments, 10);

    const careCredit = allowancesQuestions[4].answer.answer.child_and_dependent_care_credit;
    if (/^\d+$/.test(careCredit)) value += parseInt(careCredit, 10);

    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          if (question.id === 'filing_status') {
            // conditionally render spouse earned income question
            setRenderSpouseEarnedIncomeQn(renderSpouseEarnedIncomeQnKeys.includes(value));
          }
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      {
        versionHasSpouseQn && renderSpouseEarnedIncomeQn && (
        <Flex column>
          <S.QuestionLabel mb="7px" isRequired>
            If so, does your spouse also have earned income?
          </S.QuestionLabel>
          <S.QuestionWrapper mb="20px">
            <label>
              <Flex alignItems="center">
                <S.Input
                  type="radio"
                  value={isSpouseEarnedIncome}
                  checked={isSpouseEarnedIncome}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setIsSpouseEarnedIncome(!isSpouseEarnedIncome);
                  }}
                />
                <Box ml={2} />
                Yes
              </Flex>
            </label>
            <label>
              <Flex alignItems="center">
                <S.Input
                  type="radio"
                  value={!isSpouseEarnedIncome}
                  checked={!isSpouseEarnedIncome}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setIsSpouseEarnedIncome(!isSpouseEarnedIncome);
                  }}
                />
                <Box ml={2} />
                No
              </Flex>
            </label>
          </S.QuestionWrapper>
        </Flex>
        )
      }

      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowancesQuestions = (isReviewStep) => (
    <Flex column>
      <Text style={{ fontSize: '14px' }} bold mb="10px">
        Allowances
      </Text>
      <Text style={{ fontSize: '12px' }} bold mb="20px">
        If you are not exempt from withholding, please complete the following information.
      </Text>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of allowances:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateAllowances()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={allowancesQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => {
    const exemptionsOptions = getExemptionOptions(version);

    return (
      <Flex column>
        <S.QuestionWrapper mb="10px">
          <Flex mb="8px">
            <S.QuestionLabel isRequired={isExemptFromWithholding}>
              Exemptions from withholding
            </S.QuestionLabel>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="10px">
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value="exemption_from_withholding_one"
                  checked={exemptionsFromWithholding === 'exemption_from_withholding_one'}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setExemptionsFromWithholding(e.target.value);
                  }}
                />
                <Box ml={2} />
                <Flex column flex={1}>
                  <Text>{exemptionsOptions.exemption_from_withholding_one}</Text>
                </Flex>
              </Flex>
            </label>
          </Flex>
          <Flex column mb="15px">
            <label>
              <Flex>
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value="exemption_from_withholding_two"
                  checked={exemptionsFromWithholding === 'exemption_from_withholding_two'}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setExemptionsFromWithholding(e.target.value);
                  }}
                />
                <Box ml={2} />
                <Flex column flex={1}>
                  <Text>{exemptionsOptions.exemption_from_withholding_two}</Text>
                </Flex>
              </Flex>
            </label>
          </Flex>
          {exemptionsError && exemptionsFromWithholding === '' && (
          <Flex justifyContent="flex-start" mt={1}>
            <Text color="red">This is a mandatory field.</Text>
          </Flex>
          )}
        </S.QuestionWrapper>
        {exemptionsFromWithholding === 'exemption_from_withholding_two' && (
        <FormConsumer
          questions={domicileQuestion}
          {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
          onQuestionChange={({ question, value }) => {
            const questions = domicileQuestion.map((q) => {
              if (q.id !== question.id) return q;
              const answer = {
                ...q.answer,
                answer: {
                  ...q.answer.answer,
                  [q.id]: value,
                },
              };
              const updatedQuestion = { ...q, answer };
              return updatedQuestion;
            });
            setDomicileQuestion(questions);
          }}
          ref={domicileForm}
        />
        )}
      </Flex>
    );
  };

  const handleValidation = () => {
    if (step1Form && step1Form.current && step1Form.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
      if (
        allowancesForm
        && allowancesForm.current
        && allowancesForm.current.validate((error) => !!error)
      ) {
        return true;
      }
    } else {
      if (exemptionsFromWithholding === '') {
        setExemptionsError(true);
        return true;
      }
      setExemptionsError(false);

      if (
        exemptionsFromWithholding === 'exemption_from_withholding_two'
      && domicileForm
      && domicileForm.current
      && domicileForm.current.validate((error) => !!error)
      ) {
        return true;
      }
    }
    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    // Initialise data
    const stepOneData = stepOneQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let totalAllowancesTemp = 0;
    let allowancesData = resetInitialQuestions(initialAllowancesQuestions, '0')
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let exemptionsFromWithholdingTemp = '';
    let domicileData = resetInitialQuestions(stateOfDomicileInitialQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (!isExemptFromWithholding) {
      totalAllowancesTemp = calculateAllowances().toString();
      allowancesData = allowancesQuestions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    } else {
      exemptionsFromWithholdingTemp = exemptionsFromWithholding;
      domicileData = domicileQuestion
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }
    const totalAllowancesData = { total_number_of_allowances: totalAllowancesTemp };
    const exemptionData = {
      exemption: exemptionsFromWithholdingTemp,
      is_exempt: isExemptFromWithholding,
    };
    let formData = {
      ...stepOneData,
      ...totalAllowancesData,
      ...allowancesData,
      ...exemptionData,
      ...domicileData,
    };

    /*
      Inject spouse_earned_income to the form data if:
      1. If there's spouse question in the form
        - version is of 11_28_2023 or 11_18_2024
      2. The filing status is part of the keys
        - married_filing_jointly (version 11_28_2023)
        - married_filing_jointly_or_qualifying_surviving_spouse (version 11_18_2024)
    */
    if (versionHasSpouseQn && renderSpouseEarnedIncomeQnKeys.includes(formData.filing_status)) {
      formData = { ...formData, spouse_earned_income: isSpouseEarnedIncome };
    } else {
      formData = { ...formData, spouse_earned_income: null };
    }

    saveFormData(formData, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowancesQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowancesQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  useEffect(() => {
    setStepOneQuestions(step1InitialQuestions);
    setAllowancesQuestions(allowancesQuestionsWithAnswer);
  }, [version]);

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default IowaForm;
